import React, { useEffect, useMemo, useState } from 'react';
import { Calendar, Clock, ExternalLink, Euro, Eye, Users } from 'lucide-react';
import Modal from '../Modal.jsx';
import apiService from '../../services/api.js';
import { getPrestationTypeLabel } from '../../config/prestation-types.js';
import { buildPrestationPreviewSummary } from '../../utils/prestationPreviewSummary.js';
import { buildReservationTestUrl } from '../prestation-wizard/buildWizardSiteUrls.js';

function SummaryRow({ label, value, icon: Icon }) {
  return (
    <div className="flex items-start justify-between gap-3 text-sm">
      <dt className="flex items-center gap-1.5 text-gray-500">
        {Icon ? <Icon className="h-3.5 w-3.5 shrink-0" /> : null}
        {label}
      </dt>
      <dd className="text-right font-medium text-gray-900">{value}</dd>
    </div>
  );
}

export default function PrestationPreviewModal({ isOpen, onClose, commercant, prestation }) {
  const [creneaux, setCreneaux] = useState([]);
  const [loadingCreneaux, setLoadingCreneaux] = useState(false);

  useEffect(() => {
    if (!isOpen || !prestation?.id) {
      setCreneaux([]);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoadingCreneaux(true);
      try {
        const response = await apiService.getPrestationCreneaux(prestation.id);
        const list = response?.creneaux ?? response?.data ?? [];
        if (!cancelled) setCreneaux(Array.isArray(list) ? list : []);
      } catch {
        if (!cancelled) setCreneaux([]);
      } finally {
        if (!cancelled) setLoadingCreneaux(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, prestation?.id]);

  const summary = useMemo(() => {
    if (!prestation) return null;
    return buildPrestationPreviewSummary({ commercant, prestation, creneaux });
  }, [commercant, prestation, creneaux]);

  const handleTestOnSite = () => {
    if (!commercant?.id || !prestation?.id) return;
    const url = buildReservationTestUrl(commercant, prestation.id);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const formatPrix = () => {
    if (!summary) return '—';
    if (summary.prixPromo != null && summary.prixPromo < summary.prix) {
      return `${summary.prixPromo.toFixed(2)} € (promo, base ${summary.prix?.toFixed(2)} €)`;
    }
    if (summary.prix != null) return `${summary.prix.toFixed(2)} €`;
    return '—';
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Visualiser l’activité"
      maxWidth="max-w-2xl"
    >
      {!prestation || !summary ? (
        <p className="text-sm text-gray-500">Aucune activité sélectionnée.</p>
      ) : (
        <div className="space-y-6">
          <div className="flex items-start gap-3 rounded-lg border border-purple-100 bg-purple-50/50 p-4">
            <Eye className="mt-0.5 h-5 w-5 shrink-0 text-purple-700" />
            <div>
              <h4 className="text-lg font-semibold text-gray-900">{summary.nom}</h4>
              {summary.description ? (
                <p className="mt-1 text-sm text-gray-600 line-clamp-3">{summary.description}</p>
              ) : null}
              <span
                className={`mt-2 inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                  summary.actif ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                }`}
              >
                {summary.actif ? 'Actif' : 'Inactif'}
              </span>
            </div>
          </div>

          <dl className="space-y-3 rounded-lg border border-gray-200 p-4">
            <SummaryRow label="Type" value={getPrestationTypeLabel(summary.typePrestation)} />
            <SummaryRow label="Durée" value={`${summary.dureeMinutes} min`} icon={Clock} />
            <SummaryRow label="Prix" value={formatPrix()} icon={Euro} />
            <SummaryRow label="Capacité" value={`${summary.maxParticipants} max.`} icon={Users} />
            <SummaryRow label="Mode capacité" value={summary.modeCapaciteLabel} />
          </dl>

          <div className="rounded-lg border border-gray-200 p-4">
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-900">
              <Calendar className="h-4 w-4 text-indigo-600" />
              Disponibilités
            </div>
            <p className="text-xs text-gray-500">{summary.disponibiliteMode}</p>
            {loadingCreneaux ? (
              <p className="mt-2 text-xs text-gray-400">Chargement des plages…</p>
            ) : (
              <ul className="mt-3 space-y-1 text-sm text-gray-700">
                {summary.plagesLines.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            )}
            <p className="mt-3 text-sm text-indigo-800">
              {summary.slotsEstimate > 0 ? (
                <>
                  ~<strong>{summary.slotsEstimate}</strong> créneaux / jour type (pas {summary.pasMinutes} min,
                  durée {summary.dureeMinutes} min)
                </>
              ) : (
                'Estimation : aucun créneau calculable avec la configuration actuelle.'
              )}
            </p>
          </div>

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Fermer
            </button>
            <button
              type="button"
              onClick={handleTestOnSite}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-indigo-200 bg-white px-4 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-50"
            >
              <ExternalLink className="h-4 w-4" />
              Tester sur le site
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
